import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import ContentEditable from 'react-contenteditable';
import { getSelectionOffsets, setSelectionOffsets } from './caretUtils';

/**
 * react-contenteditable's componentDidUpdate calls replaceCaret(), which parks
 * the caret at the END of the editable whenever it is focused. That shows up
 * as Alt-Tab (or any focused HTML rewrite) dropping the caret at the end.
 *
 * We must NOT re-apply a saved caret on every render — that fights native
 * typing (letters appear in front of a stuck caret). Instead:
 *  1. On html prop changes: snapshot the caret during render (DOM already has
 *     the typed character + correct caret), then re-apply after commit only if
 *     replaceCaret moved it.
 *  2. On window/tab blur: freeze the last selection; on focus, restore it.
 *
 * Drag-highlight fires selectionchange on every move. Measuring that range
 * (Range.toString) lays out the field, which is the lag when the highlight
 * grows. Capture the caret once the button is up — blur-restore doesn't
 * need the in-between positions.
 */
let pointerSelecting = false;
const selectionCaptures = new Set();
let pointerSelectBound = false;

const onSelectionChange = () => {
  if (pointerSelecting) return;
  selectionCaptures.forEach((fn) => fn());
};

const bindPointerSelect = () => {
  if (pointerSelectBound || typeof document === 'undefined') return;
  pointerSelectBound = true;
  document.addEventListener(
    'pointerdown',
    (e) => {
      if (e.button === 0) pointerSelecting = true;
    },
    true
  );
  const release = () => {
    if (!pointerSelecting) return;
    pointerSelecting = false;
    selectionCaptures.forEach((fn) => fn());
  };
  document.addEventListener('pointerup', release, true);
  document.addEventListener('pointercancel', release, true);
  window.addEventListener('blur', release, true);
  document.addEventListener('selectionchange', onSelectionChange);
};

const SafeContentEditable = ({ innerRef, html, ...props }) => {
  const elRef = useRef(null);
  const committedHtmlRef = useRef(html);
  const restoreToRef = useRef(null);
  const blurSavedRef = useRef(null);
  const freezeCapturesRef = useRef(false);
  const restoringRef = useRef(false);

  const setRef = useCallback(
    (node) => {
      elRef.current = node;
      if (typeof innerRef === 'function') innerRef(node);
      else if (innerRef) innerRef.current = node;
    },
    [innerRef]
  );

  // Before ContentEditable's commit: remember where the caret is now.
  // For keystrokes the browser has already inserted the character and advanced
  // the caret — snapshotting that position is what keeps typing correct.
  if (committedHtmlRef.current !== html) {
    const el = elRef.current;
    if (
      el &&
      typeof document !== 'undefined' &&
      document.activeElement === el &&
      !document.hidden
    ) {
      const offsets = getSelectionOffsets(el);
      if (offsets) restoreToRef.current = offsets;
    }
  }

  useLayoutEffect(() => {
    if (committedHtmlRef.current === html) return;
    committedHtmlRef.current = html;

    const el = elRef.current;
    const target = restoreToRef.current;
    restoreToRef.current = null;
    if (!el || !target || document.activeElement !== el) return;

    const current = getSelectionOffsets(el);
    if (
      current &&
      current.start === target.start &&
      current.end === target.end
    ) {
      return;
    }

    restoringRef.current = true;
    setSelectionOffsets(el, target.start, target.end);
    requestAnimationFrame(() => {
      restoringRef.current = false;
    });
  }, [html]);

  const captureForBlur = useCallback(() => {
    if (pointerSelecting || freezeCapturesRef.current || restoringRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (
      typeof document !== 'undefined' &&
      typeof document.hasFocus === 'function' &&
      !document.hasFocus()
    ) {
      return;
    }
    const el = elRef.current;
    if (!el || document.activeElement !== el) return;
    const offsets = getSelectionOffsets(el);
    if (offsets) blurSavedRef.current = offsets;
  }, []);

  useEffect(() => {
    bindPointerSelect();
    selectionCaptures.add(captureForBlur);
    return () => {
      selectionCaptures.delete(captureForBlur);
    };
  }, [captureForBlur]);

  useEffect(() => {
    let unfreezeTimer = null;

    const restoreBlurSaved = () => {
      const el = elRef.current;
      const saved = blurSavedRef.current;
      if (!el?.isConnected || !saved) return;
      if (document.activeElement !== el) return;
      restoringRef.current = true;
      setSelectionOffsets(el, saved.start, saved.end);
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
    };

    const freeze = () => {
      freezeCapturesRef.current = true;
      if (unfreezeTimer != null) {
        clearTimeout(unfreezeTimer);
        unfreezeTimer = null;
      }
    };

    const unfreezeAndRestore = () => {
      freezeCapturesRef.current = true;
      restoreBlurSaved();
      requestAnimationFrame(restoreBlurSaved);
      setTimeout(restoreBlurSaved, 0);
      if (unfreezeTimer != null) clearTimeout(unfreezeTimer);
      unfreezeTimer = setTimeout(() => {
        restoreBlurSaved();
        freezeCapturesRef.current = false;
        captureForBlur();
        unfreezeTimer = null;
      }, 45);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') freeze();
      else unfreezeAndRestore();
    };

    window.addEventListener('blur', freeze);
    window.addEventListener('focus', unfreezeAndRestore);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (unfreezeTimer != null) clearTimeout(unfreezeTimer);
      window.removeEventListener('blur', freeze);
      window.removeEventListener('focus', unfreezeAndRestore);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [captureForBlur]);

  return <ContentEditable {...props} html={html} innerRef={setRef} />;
};

export default SafeContentEditable;
